import { Controller } from "@hotwired/stimulus"

/**
 * Bundle Pricing Controller
 * 
 * Handles real-time price calculations for the bundle purchase modal.
 * Manages slider updates, option toggles, and order summary updates.
 * 
 * Validates: Requirements 2.1, 2.2, 2.3, 2.4, 3.4, 5.2
 * 
 * Feature: bundle-modal-feature, Property 2: Price calculation correctness
 * Feature: bundle-modal-feature, Property 3: Discount percentage matches pricing tier
 * Feature: bundle-modal-feature, Property 4: Total price updates with option changes
 */
export default class extends Controller {
  static targets = [
    // Slider and job count
    "slider",
    "jobCount",
    // Price displays
    "originalPrice",
    "discountedPrice",
    "savings",
    "discountPercent",
    "pricePerPost",
    "savingsBadge",
    // Option inputs
    "boostCheckbox",
    "stickyCheckbox",
    "stickyPeriodInput",
    "highlightCheckbox",
    "highlightColorInput",
    "highlightTypeInput",
    // Order summary elements
    "totalPrice",
    "bundleSize",
    "addonsBreakdown",
    "summaryOriginalPrice",
    "summaryDiscountPercent",
    "summaryDiscount",
    "boostTotal",
    "stickyTotal",
    "highlightTotal",
    "summarySubtotal",
    "summaryBoostTotal",
    "summaryStickyTotal",
    "summaryHighlightTotal",
    // Submit button
    "submitButton",
    "submitJobCount",
    "submitPrice"
  ]

  static values = {
    basePrice: Number,
    discountTiers: Object,
    boostPrice: Number,
    stickyPrices: Object,
    highlightPrices: Object,
    supportPrice: Number
  }

  connect() {
    this.initializeState()
    this.updateSliderProgress()
    this.calculate()
  }

  initializeState() {
    // Initialize state from actual checkbox values in the DOM
    const boostCheckbox = document.getElementById('modal_bundle_boost')
    const stickyCheckbox = document.getElementById('modal_bundle_sticky')
    const highlightCheckbox = document.getElementById('modal_bundle_highlight')
    
    this.state = {
      jobCount: this.hasSliderTarget ? parseInt(this.sliderTarget.value) : 50,
      boost: boostCheckbox ? boostCheckbox.checked : false,
      stickyPeriod: 7,
      stickyEnabled: stickyCheckbox ? stickyCheckbox.checked : false,
      highlightType: 'standard',
      highlightEnabled: highlightCheckbox ? highlightCheckbox.checked : false,
      support: false
    }

    // Update boost visibility
    if (boostCheckbox) {
      this.state.boost = boostCheckbox.checked
      this.updateAddonVisibility('boost', this.state.boost)
    }

    // Update sticky visibility and duration options
    if (stickyCheckbox) {
      this.state.stickyEnabled = stickyCheckbox.checked
      this.updateAddonVisibility('sticky', this.state.stickyEnabled)
      const stickyOptions = document.getElementById('sticky-duration-options')
      if (stickyOptions) {
        stickyOptions.style.display = this.state.stickyEnabled ? 'block' : 'none'
      }
      // Server treats any non-blank sticky_period as sticky-enabled; clear
      // the hidden input on initial render when the checkbox is unchecked
      // so a default submit doesn't get priced as sticky.
      if (this.hasStickyPeriodInputTarget && !this.state.stickyEnabled) {
        this.stickyPeriodInputTarget.value = ""
      }
    }

    // Update highlight visibility
    if (highlightCheckbox) {
      this.state.highlightEnabled = highlightCheckbox.checked
      this.state.highlightType = this.state.highlightEnabled ? 'standard' : 'no'
      const highlightOptions = document.getElementById('highlight-color-options')
      if (highlightOptions) {
        highlightOptions.style.display = this.state.highlightEnabled ? 'block' : 'none'
      }
      // Server treats higlight_type != "no" as highlight-enabled; sync
      // the hidden input on initial render so an unchecked highlight
      // submits "no" instead of the form default "custom".
      if (this.hasHighlightTypeInputTarget) {
        this.highlightTypeInputTarget.value = this.state.highlightType
      }
      this.updateAddonVisibility('highlight', this.state.highlightEnabled)
    }
  }

  updateSlider() {
    if (!this.hasSliderTarget) return
    const value = parseInt(this.sliderTarget.value)
    this.state.jobCount = value
    if (this.hasJobCountTarget) {
      this.jobCountTarget.textContent = value
    }
    // Update slider track fill
    this.updateSliderProgress()
    this.calculate()
  }

  updateSliderProgress() {
    if (!this.hasSliderTarget) return
    const slider = this.sliderTarget
    const min = parseInt(slider.min) || 0
    const max = parseInt(slider.max) || 100
    const value = parseInt(slider.value) || 0
    const progress = ((value - min) / (max - min)) * 100
    slider.style.setProperty('--slider-progress', `${progress}%`)
  }

  toggleOption(event) {
    const target = event.currentTarget
    const optionType = target.dataset.optionType

    switch (optionType) {
      case 'boost':
        this.state.boost = target.checked
        this.updateAddonVisibility('boost', target.checked)
        break
      case 'sticky':
        this.state.stickyEnabled = target.checked
        // BundlesController#create derives @bundle.sticky purely from
        // sticky_period (blank/0 → false). Mirror the UI state into the
        // hidden input so a default-unchecked submit isn't priced as
        // sticky-enabled on the server.
        if (this.hasStickyPeriodInputTarget) {
          this.stickyPeriodInputTarget.value = target.checked
            ? String(this.state.stickyPeriod || 7)
            : ""
        }
        const stickyOptions = document.getElementById('sticky-duration-options')
        if (stickyOptions) {
          stickyOptions.style.display = this.state.stickyEnabled ? 'block' : 'none'
        }
        this.updateAddonVisibility('sticky', target.checked)
        break
      case 'highlight':
        this.state.highlightEnabled = target.checked
        this.state.highlightType = target.checked ? 'standard' : 'no'
        // Same shape as sticky: server reads higlight_type and treats
        // anything not "no" as enabled. Sync the hidden input so an
        // unchecked highlight defaults to "no" instead of "custom".
        if (this.hasHighlightTypeInputTarget) {
          this.highlightTypeInputTarget.value = this.state.highlightType
        }
        const highlightOptions = document.getElementById('highlight-color-options')
        if (highlightOptions) {
          highlightOptions.style.display = target.checked ? 'block' : 'none'
        }
        this.updateAddonVisibility('highlight', target.checked)
        break
    }
    this.calculate()
  }

  selectStickyDuration(event) {
    const period = parseInt(event.currentTarget.value)
    this.state.stickyPeriod = period
    if (this.hasStickyPeriodInputTarget) {
      this.stickyPeriodInputTarget.value = period
    }
    this.calculate()
  }


  selectColor(event) {
    event.preventDefault()
    const button = event.currentTarget
    const color = button.dataset.color
    if (this.hasHighlightColorInputTarget) {
      this.highlightColorInputTarget.value = color
    }
    const colorButtons = document.querySelectorAll('.bundle-modal__color-btn')
    colorButtons.forEach(btn => {
      btn.classList.remove('bundle-modal__color-btn--selected')
      btn.innerHTML = ''
    })
    button.classList.add('bundle-modal__color-btn--selected')
    button.innerHTML = '<span class="bundle-modal__color-check">✓</span>'
  }

  updateAddonVisibility(addonType, isVisible) {
    if (!this.hasAddonsBreakdownTarget) return
    const addonRow = this.addonsBreakdownTarget.querySelector(`[data-addon="${addonType}"]`)
    if (addonRow) {
      addonRow.style.display = isVisible ? 'flex' : 'none'
    }
  }

  /**
   * Main calculation method - follows reference design (Bundlemodal.html):
   * 1. Base bundle price = base_price × job_count
   * 2. Discounted bundle price = base_bundle_price × (1 - discount_percent)
   * 3. Add-ons are calculated separately and added to total
   * 4. Price per post = discounted_bundle_price / job_count (base only, no add-ons)
   */
  calculate() {
    const { jobCount, boost, stickyEnabled, stickyPeriod, highlightEnabled } = this.state
    const discountPercent = this.getDiscountPercent(jobCount)

    // Base bundle price (without add-ons) - this gets the discount
    const baseBundlePrice = this.basePriceValue * jobCount
    const discountAmount = Math.round(baseBundlePrice * discountPercent)
    const discountedBundlePrice = baseBundlePrice - discountAmount

    // Add-on prices (NOT discounted, added on top)
    const boostPrice = boost ? (this.boostPriceValue || 50) : 0
    const stickyPrice = stickyEnabled ? this.getStickyPrice(stickyPeriod) : 0
    const highlightPrice = highlightEnabled ? this.getHighlightPrice('standard') : 0

    // Add-on totals for display
    const boostTotal = boost ? boostPrice * jobCount : 0
    const stickyTotal = stickyEnabled ? stickyPrice * jobCount : 0
    const highlightTotal = highlightEnabled ? highlightPrice * jobCount : 0

    // Total = discounted bundle + all add-ons
    const totalPrice = discountedBundlePrice + boostTotal + stickyTotal + highlightTotal

    // Price per post = discounted base price per post (without add-ons)
    const pricePerPost = Math.round(discountedBundlePrice / jobCount)

    this.updatePriceDisplays({
      jobCount,
      originalPrice: baseBundlePrice,
      discountedBundlePrice,
      totalPrice,
      savings: discountAmount,
      discountPercent,
      pricePerPost,
      boostTotal,
      stickyTotal,
      highlightTotal
    })
  }

  getDiscountPercent(jobCount) {
    const tiers = this.discountTiersValue
    if (!tiers || !tiers[jobCount]) return 0
    return tiers[jobCount].percent || 0
  }

  getStickyPrice(period) {
    const prices = this.stickyPricesValue
    if (!prices) return 0
    switch (period) {
      case 1: return prices.day1 || 0
      case 3: return prices.day3 || 0
      case 7: return prices.day7 || 0
      case 14: return prices.day14 || 0
      case 30: return prices.day30 || 0
      default: return 0
    }
  }

  getHighlightPrice(type) {
    const prices = this.highlightPricesValue
    if (!prices) return 0
    switch (type) {
      case 'standard': return prices.standard || 0
      case 'custom': return prices.custom || 0
      default: return 0
    }
  }

  updatePriceDisplays({ jobCount, originalPrice, discountedBundlePrice, totalPrice, savings, discountPercent, pricePerPost, boostTotal, stickyTotal, highlightTotal }) {
    if (this.hasBundleSizeTarget) {
      this.bundleSizeTarget.textContent = jobCount
    }

    // Slider section displays
    if (this.hasOriginalPriceTarget) {
      this.originalPriceTarget.textContent = "$" + this.formatPrice(originalPrice)
    }
    if (this.hasDiscountedPriceTarget) {
      this.discountedPriceTarget.textContent = "$" + this.formatPrice(discountedBundlePrice)
    }
    if (this.hasSavingsTarget) {
      this.savingsTarget.textContent = this.formatPrice(savings)
    }
    if (this.hasDiscountPercentTarget) {
      this.discountPercentTarget.textContent = this.formatPercent(discountPercent)
    }
    if (this.hasPricePerPostTarget) {
      this.pricePerPostTarget.textContent = "$" + this.formatPrice(pricePerPost)
    }

    // Order summary displays
    if (this.hasTotalPriceTarget) {
      this.totalPriceTarget.textContent = "$" + this.formatPrice(totalPrice)
    }
    if (this.hasSummaryOriginalPriceTarget) {
      this.summaryOriginalPriceTarget.textContent = "$" + this.formatPrice(originalPrice)
    }
    if (this.hasSummaryDiscountPercentTarget) {
      this.summaryDiscountPercentTarget.textContent = this.formatPercent(discountPercent)
    }
    if (this.hasSummaryDiscountTarget) {
      this.summaryDiscountTarget.textContent = this.formatPrice(savings)
    }
    if (this.hasSummarySubtotalTarget) {
      this.summarySubtotalTarget.textContent = "$" + this.formatPrice(discountedBundlePrice)
    }

    // Add-on totals
    if (this.hasBoostTotalTarget) {
      this.boostTotalTarget.textContent = "+$" + this.formatPrice(boostTotal)
    }
    if (this.hasSummaryBoostTotalTarget) {
      this.summaryBoostTotalTarget.textContent = "+$" + this.formatPrice(boostTotal)
    }
    if (this.hasStickyTotalTarget) {
      this.stickyTotalTarget.textContent = "+$" + this.formatPrice(stickyTotal)
    }
    if (this.hasSummaryStickyTotalTarget) {
      this.summaryStickyTotalTarget.textContent = "+$" + this.formatPrice(stickyTotal)
    }
    if (this.hasHighlightTotalTarget) {
      this.highlightTotalTarget.textContent = "+$" + this.formatPrice(highlightTotal)
    }
    // ... existing code ...
    if (this.hasSummaryHighlightTotalTarget) {
      this.summaryHighlightTotalTarget.textContent = "+$" + this.formatPrice(highlightTotal)
    }

    // Update submit button (reference design: button text is just "Complete Purchase")
    this.updateSubmitButton(totalPrice, jobCount)
  }

  updateSubmitButton(price, jobCount) {
    // Mockup: a lock icon + "Complete Purchase" (no inline price). Set via
    // innerHTML so the icon survives this rewrite (textContent would strip it).
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.innerHTML = '<span class="material-symbols-outlined text-[20px]" aria-hidden="true">lock</span>Complete Purchase'
    }
  }

  formatPrice(amount) {
    return Math.round(amount).toLocaleString()
  }

  formatPercent(decimal) {
    return Math.round(decimal * 100) + '%'
  }

  getCurrentPrices() {
    const { jobCount, boost, stickyEnabled, stickyPeriod, highlightEnabled } = this.state
    const discountPercent = this.getDiscountPercent(jobCount)
    const baseBundlePrice = this.basePriceValue * jobCount
    const discountAmount = Math.round(baseBundlePrice * discountPercent)
    const discountedBundlePrice = baseBundlePrice - discountAmount

    const boostTotal = boost ? (this.boostPriceValue || 50) * jobCount : 0
    const stickyTotal = stickyEnabled ? this.getStickyPrice(stickyPeriod) * jobCount : 0
    const highlightTotal = highlightEnabled ? this.getHighlightPrice('standard') * jobCount : 0

    const totalPrice = discountedBundlePrice + boostTotal + stickyTotal + highlightTotal

    return {
      jobCount,
      originalPrice: baseBundlePrice,
      discountedBundlePrice,
      totalPrice,
      savings: discountAmount,
      discountPercent,
      pricePerPost: Math.round(discountedBundlePrice / jobCount)
    }
  }
}
